import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const HomePage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for homepage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Durbanville Painters",
      "description": "Get quotes from the best painting contractors in Durbanville, Western Cape. Professional interior, exterior & roof painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Durbanville",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8347",
        "longitude": "18.6546"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "-33.8347",
          "longitude": "18.6546"
        },
        "geoRadius": "10000"
      },
      "services": [
        "Interior Painting",
        "Exterior Painting", 
        "Roof Painting",
        "Commercial Painting"
      ],
      "areaServed": [
        "Durbanville",
        "Bellville",
        "Parow",
        "Goodwood",
        "Elsies River",
        "Kuils River",
        "Brackenfell",
        "Kraaifontein",
        "Milnerton",
        "Table View",
        "Bloubergstrand"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Durbanville Painters - Best Painting Contractors Western Cape | Get Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get quotes from the best painting contractors in Durbanville, Western Cape. Professional interior, exterior & roof painting services. Highest quality workmanship guaranteed.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting',
      description: 'Transform your indoor spaces with professional interior painting services. Quality finishes that last.',
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior Painting',
      description: 'Protect and beautify your property exterior with weather-resistant paints and expert application.',
    },
    {
      icon: 'ri-home-2-line',
      title: 'Roof Painting',
      description: 'Extend your roof\'s lifespan and improve your home\'s appearance with professional roof painting.',
    },
    {
      icon: 'ri-office-building-line',
      title: 'Commercial Painting',
      description: 'Professional painting services for offices, retail spaces, and commercial properties.',
    },
  ];

  const areas = [
    { name: 'Bellville', path: '/bellville-painters' },
    { name: 'Parow', path: '/parow-painters' },
    { name: 'Goodwood', path: '/goodwood-painters' },
    { name: 'Elsies River', path: '/elsies-river-painters' },
    { name: 'Kuils River', path: '/kuils-river-painters' },
    { name: 'Brackenfell', path: '/brackenfell-painters' },
    { name: 'Kraaifontein', path: '/kraaifontein-painters' },
    { name: 'Milnerton', path: '/milnerton-painters' },
    { name: 'Table View', path: '/table-view-painters' },
    { name: 'Bloubergstrand', path: '/bloubergstrand-painters' },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-award-line',
      title: 'Highest Quality',
      description: 'We connect you with the best painting contractors who deliver exceptional workmanship.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Vetted Professionals',
      description: 'All our painting contractors are thoroughly screened and verified for quality and reliability.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Best Prices',
      description: 'Compare quotes from multiple contractors to ensure you get the best value for your money.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Expert Support',
      description: 'Our team helps you find the perfect painting contractor for your specific needs.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/cb807e70a7682a27ce7e7ae7023b07ae.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block">Durbanville</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get a quote from the best.
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto opacity-90">
            Connect with Durbanville's top-rated painting contractors. We find you the highest quality painters 
            who deliver exceptional workmanship for your home or business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Quotes
            </Link>
            <a 
              href="#services" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting quotes from the best painting contractors in Durbanville is simple and fast
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Submit Your Request</h3>
              <p className="text-gray-600">
                Tell us about your painting project and requirements. It takes just 2 minutes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Get Matched</h3>
              <p className="text-gray-600">
                We connect you with up to 3 vetted painting contractors in your area.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Choose the Best</h3>
              <p className="text-gray-600">
                Compare quotes and choose the contractor that best fits your needs and budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our network of expert contractors provides comprehensive painting solutions for all your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100">
                <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Durbanville Painters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not just another painting company - we're your trusted partner in finding the best contractors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional painting contractors available throughout Durbanville and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {areas.map((area) => (
              <Link
                key={area.path}
                to={area.path}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 text-center border border-gray-100 hover:border-[#e9f638] group cursor-pointer"
              >
                <h3 className="font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                  {area.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">Painters</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it - hear from satisfied customers across Durbanville
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Quotes Today
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your property? Get connected with the best painting contractors in Durbanville
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default HomePage;